import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import getAPIbaseURL from "../../utils/helpers";

const API_URL = `${getAPIbaseURL()}/shifts`;

// Create shift
export const createShift = createAsyncThunk(
  "shifts/createShift",
  async (shiftData, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/`, shiftData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error creating shift");
    }
  }
);

export const publishShifts = createAsyncThunk(
  "shifts/publish",
  async ({ shifts = [], deleteIds = [] }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/publish`, { shifts, delete_ids: deleteIds }, { headers: { Authorization: `Bearer ${token}` } });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error publishing shifts");
    }
  }
);

// Fetch all shifts
export const fetchShifts = createAsyncThunk(
  "shifts/fetchShifts",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching shifts");
    }
  }
);

// Fetch filtered Shifts
export const fetchFilteredShifts = createAsyncThunk(
  "shifts/fetchFilteredShifts",
  async (filters, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams(filters).toString();
      const response = await axios.get(`${API_URL}/filter?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching filtered shifts");
    }
  }
);

// Fetch shifts by user ID
export const fetchShiftsByUser = createAsyncThunk(
  "shifts/fetchShiftsByUser",
  async (userId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching user shifts");
    }
  }
);

// Update shift
export const updateShift = createAsyncThunk(
  "shifts/updateShift",
  async ({ shiftId, updateData }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/${shiftId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error updating shift");
    }
  }
);

// Delete shift
export const deleteShift = createAsyncThunk(
  "shifts/deleteShift",
  async (shiftId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return shiftId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error deleting shift");
    }
  }
);

// Fetch shift by ID
export const fetchShiftById = createAsyncThunk(
  "shifts/fetchShiftById",
  async (shiftId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching shift");
    }
  }
);

// Shift change requests
export const requestShiftChange = createAsyncThunk(
  "shifts/requestShiftChange",
  async (requestData, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/change-request`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error requesting shift change");
    }
  }
);

export const fetchNextShift = createAsyncThunk(
  "shifts/fetchNextShift",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/next`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching next shift");
    }
  }
);

export const approveShiftChangeRequest = createAsyncThunk(
  "shifts/approveShiftChangeRequest",
  async ({ requestId, status }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/change-request/${requestId}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error approving/rejecting shift request");
    }
  }
);

export const fetchShiftChangeRequests = createAsyncThunk(
  "shifts/fetchShiftChangeRequests",
  async (_, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/change-request`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || "Error fetching shift change requests");
    }
  }
);

const shiftsSlice = createSlice({
  name: "shifts",
  initialState: {
    items: [],
    isLoading: false,
    changeRequests: [],
    error: null,
    message: null,
  },
  reducers: {
    clearShiftState: (state) => {
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items.push(action.payload);
        state.message = "Shift created successfully";
      })
      .addCase(createShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(publishShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(publishShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = state.items.filter((shift) => !action.payload.deleted_ids.includes(shift.id));
        state.items.push(...action.payload.created);
        state.message = "Shifts published successfully";
      })
      .addCase(publishShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFilteredShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchFilteredShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchShiftsByUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShiftsByUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchShiftsByUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex((s) => s.id === updated.id);
        if (index !== -1) {
          state.items[index] = updated;
        }
        state.message = "Shift updated successfully";
      })
      .addCase(updateShift.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchShiftById.fulfilled, (state, action) => {
        const found = action.payload;
        const exists = state.items.find((s) => s.id === found.id);
        if (!exists) {
          state.items.push(found);
        }
      })
      .addCase(requestShiftChange.fulfilled, (state, action) => {
        state.changeRequests.push(action.payload);
        state.message = "Shift change request submitted.";
      })
      .addCase(approveShiftChangeRequest.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.changeRequests.findIndex(r => r.id === updated.id);
        if (index !== -1) state.changeRequests[index] = updated;
        state.message = `Request ${updated.status.toLowerCase()}`;
      })
      .addCase(fetchShiftChangeRequests.fulfilled, (state, action) => {
        state.changeRequests = action.payload;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.items = state.items.filter((shift) => shift.id !== action.payload);
        state.message = "Shift deleted successfully";
      })
      .addMatcher(
        (action) =>
          action.type.startsWith("shifts/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.error = action.payload;
          state.isLoading = false;
        }
      );
  }
});

export const { clearShiftState } = shiftsSlice.actions;
export default shiftsSlice.reducer;
